import { Platform } from "../constants";

import { CopyButton } from "./components/copy-button";
import { PinterestCampaignImage } from "./components/pinterest-campaign-image";
import { PinterestCatalogSalesImage } from "./components/pinterest-catalog-sales-image";
import { SetupSection } from "./components/setup-section";
import { Code, OrderedList } from "./components/styled";
import { ConstructAndCopyUrl, URLVerification } from "./components/utms";
import { type PlatformDetails } from "./platform-details";

const PLATFORM = Platform.PINTEREST;

export const PinterestDetails: PlatformDetails = {
  northbeamDocUrl:
    "https://docs.northbeam.io/docs/setting-up-pinterest-ads-for-northbeam",
  officialDocUrl: "",
  additionalDetails: [
    {
      title: "What does the integration do?",
      /* eslint-disable max-len */
      content:
        "Northbeam natively integrates with the Pinterest Ads API. By doing so, we pull all your current and historical Campaigns, Adsets, and Ads from your account, along with a list of useful metrics. Notably, we pull impressions, which fuel our view model and unlock our Clicks + Modeled Views attribution model. We also pull ad spend and list of in-platform conversion & creative metrics to help you with decision making.",
    },
  ],
};

export const PinterestSetup = ({
  connectionContent,
}: {
  connectionContent: React.ReactNode;
}) => (
  <>
    <SetupSection title="Log into Pinterest to connect integration">
      {connectionContent}
    </SetupSection>
    <SetupSection title="Follow these steps for all campaigns type UTMs except ‘Catalog Campaigns’">
      <OrderedList variant="upper-alpha">
        <>Navigate to the Ad level</>
        <>Paste the parameters below at the end of all ’Destination URL’s’</>
      </OrderedList>
      <Code block>
        {`
        nbt=nb:pinterest::{campaignid}:{adgroupid}:{adid}&nb_kwd={keyword}&nb_device={device}&nb_platform=pinterest&nb_pn={product_name}&nb_ppgid={promoted_product_group_id}&nb_pid={product_id}&nb_ppid={product_partition_id}&utm_medium=paid&utm_source=pinterest&utm_campaign={campaign_name}&utm_term={adgroup_name}
        `}
      </Code>
      <CopyButton
        value={`
        nbt=nb:pinterest::{campaignid}:{adgroupid}:{adid}&nb_kwd={keyword}&nb_device={device}&nb_platform=pinterest&nb_pn={product_name}&nb_ppgid={promoted_product_group_id}&nb_pid={product_id}&nb_ppid={product_partition_id}&utm_medium=paid&utm_source=pinterest&utm_campaign={campaign_name}&utm_term={adgroup_name}
        `}
      >
        Copy script to clipboard
      </CopyButton>
    </SetupSection>
    <SetupSection title="Follow these steps ‘Catalog Campaigns’ UTMS">
      <div>
        <OrderedList variant="upper-alpha">
          <>In the ‘Ad Group Details’, navigate to ‘Product Group Details’</>
          <div className="inline-table">
            <div className="flex gap-2 items-center">
              <span>
                In the ‘Ad Group Details’, navigate to ‘Product Group Details’
              </span>
              <Code>{"{lpurl}"}</Code>
              <span>followed by the UTM parameters below</span>
            </div>
          </div>
        </OrderedList>
        <div className="flex gap-2 items-center">
          <b>Note:</b>
          <span>
            If you’re using existing parameters, you can insert our parameter at
            the end. Be sure to use an ampersand
          </span>
          <Code>&</Code>
          <span>instead of a question mark </span>
          <Code>?</Code>
        </div>
      </div>
      <Code block>
        {
          "{lpurl}?nbt=nb:pinterest::{campaignid}:{adgroupid}:{adid}&nb_kwd={keyword}&nb_device={device}&nb_platform=pinterest&nb_pn={product_name}&nb_ppgid={promoted_product_group_id}&nb_pid={product_id}&nb_ppid={product_partition_id}&utm_medium=paid&utm_source=pinterest&utm_campaign={campaign_name}&utm_term={adgroup_name}"
        }
      </Code>{" "}
      <CopyButton value="{lpurl}?nbt=nb:pinterest::{campaignid}:{adgroupid}:{adid}&nb_kwd={keyword}&nb_device={device}&nb_platform=pinterest&nb_pn={product_name}&nb_ppgid={promoted_product_group_id}&nb_pid={product_id}&nb_ppid={product_partition_id}&utm_medium=paid&utm_source=pinterest&utm_campaign={campaign_name}&utm_term={adgroup_name}">
        Copy script to clipboard
      </CopyButton>
    </SetupSection>
    <SetupSection title="Paste the destination URL - and copy the new URL with Northbeam’s URL parameters">
      <OrderedList variant="upper-alpha">
        <div className="inline-table space-y-2">
          <ConstructAndCopyUrl platform={PLATFORM} />
        </div>
        <>
          <i>
            If you are not using ‘Catalog Sales’ as your campaign objective, you
            can set the UTMs in the location here:
          </i>
          <div className="h-2" />
          <PinterestCampaignImage />
        </>
        <>
          <i>If you are using ‘Catalog sales’ you can set the UTMs here:</i>
          <div className="h-2" />
          <PinterestCatalogSalesImage />
        </>
      </OrderedList>
    </SetupSection>
    <URLVerification platform={PLATFORM} />
  </>
);
